<template>
  <div class="form-skeleton-wrapper">
    <div class="form-skeleton-container" v-if="isLoading">
      <div v-for="(element, index) in elements" :key="index" :class="getElementClass(element)">
        <template v-if="element.type === 'label'">
          <div class="skeleton-block label-skeleton" :style="getElementStyle(element)"></div>
        </template>
        <template v-else-if="element.type === 'input'">
          <div class="skeleton-block input-skeleton" :style="getElementStyle(element)"></div>
        </template>
        <template v-else-if="element.type === 'textarea'">
          <div class="skeleton-block textarea-skeleton" :style="getElementStyle(element)"></div>
        </template>
        <template v-else-if="element.type === 'button'">
          <div class="skeleton-block button-skeleton" :style="getElementStyle(element)"></div>
        </template>
        <template v-else-if="element.type === 'circle'">
          <div class="skeleton-block circle-skeleton" :style="getElementStyle(element)"></div>
        </template>
        <template v-else-if="element.type === 'block'">
          <div class="skeleton-block generic-block-skeleton" :style="getElementStyle(element)"></div>
        </template>
      </div>
    </div>
    <slot v-else></slot>
  </div>
</template>

<script>
export default {
  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
    /**
     * Defines the structure of the skeleton.
     * Each object in the array represents a skeleton element.
     *
     * Example:
     * [
     * { type: 'label', width: '50%' },
     * { type: 'input' },
     * { type: 'label', width: '40%' },
     * { type: 'input' },
     * { type: 'textarea' },
     * { type: 'button', width: '150px' },
     * { type: 'block', width: '80%', height: '20px' },
     * { type: 'circle', size: '30px' }
     * ]
     *
     * Supported types: 'label', 'input', 'textarea', 'button', 'circle', 'block'.
     *
     * Optional properties for customization:
     * - width: (string, e.g., '50%', '100px')
     * - height: (string, e.g., '18px', '48px')
     * - size: (string, for 'circle' type, e.g., '20px')
     * - gap: (boolean, if true, adds a gap before this element to simulate spacing)
     */
    elements: {
      type: Array,
      default: () => [
        { type: 'label', width: '50%' },
        { type: 'input' },
        { type: 'label', width: '40%' },
        { type: 'input' },
        { type: 'label', width: '60%' },
        { type: 'textarea' },
        { type: 'button', width: '120px', height: '40px' },
      ],
    },
  },
  methods: {
    getElementClass(element) {
      return {
        'gap-before': element.gap,
        'centered': element.centered,
      };
    },
    getElementStyle(element) {
      const style = {};
      if (element.width) {
        style.width = element.width;
      }
      if (element.height) {
        style.height = element.height;
      }
      if (element.size && element.type === 'circle') {
        style.width = element.size;
        style.height = element.size;
      }
      return style;
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.form-skeleton-wrapper {
  width: 100%;
}

.common-skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.skeleton-block {
  border-radius: 4px;
  background: linear-gradient(90deg, #F1EFEF -24.18%, #F9F8F8 50.26%, #E7E5E5 114.84%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  flex-shrink: 0;
  margin-bottom: 8px;
}

.label-skeleton {
  width: 50%;
  height: 18px;
}

.input-skeleton {
  width: 100%;
  height: 40px;
}

.textarea-skeleton {
  width: 100%;
  height: 100px;
}

.button-skeleton {
  width: 100px;
  height: 20px;
  border-radius: 8px;
}

.circle-skeleton {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.generic-block-skeleton {
  width: 100%;
  height: 20px;
}

.gap-before {
  margin-top: 20px;
}

.centered {
  display: flex;
  justify-content: center;
}
</style>
